import { formatDateTime, formatNumber } from '../../../utils/formatUtils';
import {
  ACTIVE_CONSUMPTION_STATUSES,
  BLOCKING_CONSUMPTION_STATUSES,
  CONSUMPTION_ITEM_STATUS_LABELS,
  CONSUMPTION_STATUS_LABELS,
  CONSUMPTION_UNIT_STATUS_LABELS,
  GENERATE_CONSUMPTION_ORDER_STATUSES,
  MESSAGES,
} from '../constants/consumptionsConstants';

export const getConsumptionApiErrorMessage = (
  error,
  fallbackMessage = 'No se pudo completar la operación'
) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallbackMessage;
  }

  if (typeof data === 'string') {
    return data;
  }

  const candidates = [data.error, data.detail, data.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().replace(/^Error de validación:\s*/i, '');
    }
  }

  return fallbackMessage;
};

export const formatConsumptionStatus = (status) =>
  CONSUMPTION_STATUS_LABELS[status] || status || '-';

export const formatConsumptionItemStatus = (status) =>
  CONSUMPTION_ITEM_STATUS_LABELS[status] || status || '-';

export const formatConsumptionUnitStatus = (status) =>
  CONSUMPTION_UNIT_STATUS_LABELS[status] || status || '-';

export const formatConsumptionDate = (value) => formatDateTime(value);

export const formatConsumptionQuantity = (value) =>
  formatNumber(value, { decimals: 3, fallback: '0' });

export const filterConsumptions = (consumptions = [], searchTerm = '') => {
  const normalized = searchTerm?.trim().toLowerCase();

  if (!normalized) {
    return consumptions;
  }

  return consumptions.filter((consumption) => {
    const number = consumption.consumption_number?.toLowerCase() || '';
    const orderNumber = consumption.order_number?.toLowerCase() || '';
    const orderUuidPrefix = consumption.uuid_external_order?.slice(0, 8).toLowerCase() || '';
    const warehouseLabel = consumption.warehouse_label?.toLowerCase() || '';
    const statusLabel = formatConsumptionStatus(consumption.status).toLowerCase();
    const responsible = consumption.responsible_label?.toLowerCase() || '';

    return (
      number.includes(normalized) ||
      orderNumber.includes(normalized) ||
      orderUuidPrefix.includes(normalized) ||
      warehouseLabel.includes(normalized) ||
      statusLabel.includes(normalized) ||
      responsible.includes(normalized)
    );
  });
};

export const hasActiveConsumption = (consumptions = []) =>
  consumptions.some((consumption) =>
    ACTIVE_CONSUMPTION_STATUSES.includes(consumption.status)
  );

export const hasBlockingConsumption = (consumptions = []) =>
  consumptions.some((consumption) =>
    BLOCKING_CONSUMPTION_STATUSES.includes(consumption.status)
  );

export const getActiveConsumption = (consumptions = []) =>
  consumptions.find((consumption) =>
    ACTIVE_CONSUMPTION_STATUSES.includes(consumption.status)
  );

export const getBlockingConsumption = (consumptions = []) =>
  consumptions.find((consumption) =>
    BLOCKING_CONSUMPTION_STATUSES.includes(consumption.status)
  );

const toQuantity = (value) => Number(value ?? 0);

const isOrderItemFullyConsumed = (item) => {
  const requested = toQuantity(item?.requested_quantity);
  const consumed = toQuantity(item?.consumed_quantity);
  return item?.status === 'consumed' || consumed >= requested;
};

export const getItemConsumableQuantity = (item) => {
  if (isOrderItemFullyConsumed(item)) {
    return 0;
  }

  const requested = toQuantity(item?.requested_quantity);
  const missing = toQuantity(item?.missing_quantity);
  const consumed = toQuantity(item?.consumed_quantity);

  if (requested <= 0) {
    return 0;
  }

  return Math.max(0, requested - missing - consumed);
};

export const getReadyToConsumeOrderItems = (order) =>
  (order?.items || []).filter((item) => {
    if (item?.status === 'cancelled') {
      return false;
    }

    return getItemConsumableQuantity(item) > 0;
  });

export const isPartialConsumptionScenario = (order) => {
  const activeItems = (order?.items || []).filter((item) => item?.status !== 'cancelled');
  const consumableItems = getReadyToConsumeOrderItems(order);
  const pendingItems = activeItems.filter(
    (item) => !isOrderItemFullyConsumed(item) && getItemConsumableQuantity(item) === 0
  );

  if (consumableItems.length === 0) {
    return false;
  }

  return (
    pendingItems.length > 0 ||
    consumableItems.some((item) => toQuantity(item?.missing_quantity) > 0)
  );
};

export const getGenerateConsumptionButtonLabel = (order) =>
  isPartialConsumptionScenario(order)
    ? 'Consumir materiales listos'
    : 'Generar consumo';

export const canGenerateConsumptionFromOrder = (order, orderConsumptions = []) => {
  if (!order?.uuid) {
    return false;
  }

  if (!GENERATE_CONSUMPTION_ORDER_STATUSES.includes(order.status)) {
    return false;
  }

  if (!order.consumption_authorized) {
    return false;
  }

  if (getReadyToConsumeOrderItems(order).length === 0) {
    return false;
  }

  return !hasBlockingConsumption(orderConsumptions);
};

export const getExternalOrderLabel = (order, orderUuid) => {
  if (order?.order_number) {
    return order.order_number;
  }

  if (order?.number) {
    return order.number;
  }

  if (orderUuid) {
    return orderUuid.slice(0, 8);
  }

  return '-';
};

export const mapConsumptionToDisplay = (
  consumption,
  {
    ordersByUuid = {},
    warehousesByUuid = {},
    usersByUuid = {},
  } = {}
) => {
  const order = ordersByUuid[consumption.uuid_external_order];
  const warehouse = warehousesByUuid[consumption.uuid_warehouse];
  const createdByUser = usersByUuid[consumption.uuid_created_by];

  const responsibleLabel =
    consumption.delivery_responsible ||
    createdByUser?.name ||
    createdByUser?.username ||
    '-';

  return {
    ...consumption,
    order_number: getExternalOrderLabel(order, consumption.uuid_external_order),
    warehouse_label: warehouse?.name || warehouse?.code || '-',
    status_label: formatConsumptionStatus(consumption.status),
    responsible_label: responsibleLabel,
    created_at_label: formatConsumptionDate(consumption.created_at),
    delivered_at_label: formatConsumptionDate(consumption.delivered_at),
  };
};

export const buildOrdersByUuid = (orders = []) =>
  orders.reduce((acc, order) => {
    if (order?.uuid) {
      acc[order.uuid] = order;
    }
    return acc;
  }, {});

export const buildWarehousesByUuid = (warehouses = []) =>
  warehouses.reduce((acc, warehouse) => {
    if (warehouse?.uuid) {
      acc[warehouse.uuid] = warehouse;
    }
    return acc;
  }, {});

export const buildUsersByUuid = (users = []) =>
  users.reduce((acc, user) => {
    if (user?.uuid) {
      acc[user.uuid] = user;
    }
    return acc;
  }, {});

export const getInitialDeliverState = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return {
    delivery_responsible: '',
    delivery_address: '',
    delivery_date: now.toISOString().split('T')[0],
    delivery_time: `${hours}:${minutes}`,
    observations: '',
  };
};

export const buildDeliverPayload = (formData) => {
  const payload = {
    delivery_responsible: formData.delivery_responsible.trim(),
    delivery_address: formData.delivery_address.trim(),
  };

  if (formData.delivery_date && formData.delivery_time) {
    payload.delivered_at = `${formData.delivery_date}T${formData.delivery_time}:00`;
  }

  const observations = formData.observations?.trim();
  if (observations) {
    payload.observations = observations;
  }

  return payload;
};

export const validateDeliverConsumptionForm = (formData) => {
  const errors = {};

  if (!formData.delivery_responsible?.trim()) {
    errors.delivery_responsible = MESSAGES.ERROR.REQUIRED_RESPONSIBLE;
  }

  if (!formData.delivery_address?.trim()) {
    errors.delivery_address = MESSAGES.ERROR.REQUIRED_ADDRESS;
  }

  if (!formData.delivery_date) {
    errors.delivery_date = MESSAGES.ERROR.REQUIRED_DATE;
  }

  if (!formData.delivery_time) {
    errors.delivery_time = MESSAGES.ERROR.REQUIRED_TIME;
  }

  return errors;
};

export const validateReverseConsumptionForm = (formData) => {
  const errors = {};

  if (!formData.reason?.trim()) {
    errors.reason = MESSAGES.ERROR.REQUIRED_REASON;
  }

  return errors;
};

export const buildReversePayload = (formData) => {
  const payload = {
    reason: formData.reason.trim(),
  };

  const observations = formData.observations?.trim();
  if (observations) {
    payload.observations = observations;
  }

  return payload;
};

export const buildGenerateConsumptionPayload = (formData) => {
  const payload = {};

  if (formData.uuid_warehouse) {
    payload.uuid_warehouse = formData.uuid_warehouse;
  }

  const observations = formData.observations?.trim();
  if (observations) {
    payload.observations = observations;
  }

  return payload;
};
